import { Table, Button, Space, Input, Tooltip, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { InitData, WipeData } from '../../store/system/showPage';
import fetchApi from '../../utils/api/fetchApi';
import antMessage from '../../utils/extend/AntdNotification';
import WebBehaviors, {
    behaviorsDataType,
    deleteButtonConfigType,
} from './behaviors/WebBehaviorsNew';
import systematic from '../../config/systematic';
import DownloadExcel from '../report/DownloadExcel';
import WebUniversalLogger from './WebUniversalLogger';
import { LogType } from '../../wmCommonLibrary/wmDeclareTypes';

// ------------------- 类型定义 -------------------
export declare type serviceIdType = 'bm' | 'pay' | 'business';

/**
 * 根据服务ID获取对应的基础服务地址
 * @param serviceId 服务标识
 * @returns 服务基地址
 */


/** WebUniversal 扩展属性类型 */
export declare type WUExtendPropsType = {
    /** 图片数据数组名称 */
    arrayFilesName?: string;
    /** 编辑组件 */
    EditComponent?: any;
    /** 文件上传组件 */
    UploadFileComponent?: any;
    /** 标题 */
    titleName: string;
    /** api服务id */
    apiId: string;
    /** 主键名称 */
    primaryKeyName?: string;
    /** 是否显示文件上传按键 */
    showUploadFile?: boolean;
    /** 是否显示增加键 */
    showAdd?: boolean;
    /** 是否显示查询 */
    showQuery?: boolean;
    /** 查询的提示信息 */
    queryTitle?: string;
    /** 是否显示图片 */
    showImage?: boolean;
    /** 显示useBak */
    showUseBak?: boolean;
    /** 是否显示状态 */
    showStates?: boolean;
    /** 是否显示操作区域 */
    showOperation?: boolean;
    /** 是否显示删除键 */
    showDelete?: boolean;
    /** 是否显示编辑键 */
    showEdit?: boolean;
    /** 是否显示打印键 */
    showPrint?: boolean;
    /** 是否禁用增加键 */
    disabledAdd?: boolean;
    /** 是否禁用上传键 */
    disabledUpload?: boolean;
    /** 上传文件的数据 */
    uploadSendData?: any;
    /** 表头 */
    columns: Array<any>;
    /** 页面传送其它数据 */
    sendPageData?: any;
    /** 是否有子表格？
     * （表格内容较多，如参数expandableDataName：undefined时，可分两行展示）
     */
    isExpandable?: boolean;
    /** 子表格表头 */
    expandableColumns?: Array<any>;
    /** 子表格数据数组对象名称，当为undefined时，如表格内容较多，可分两行展示 */
    expandableDataName?: any;
    /** 包含主数据内容 */
    isExpandableDateIncludingMasterData?: boolean;
    /** 总结栏 */
    summaryRender?: any;
    /** 删除按钮配置 */
    deleteButtonConfig?: deleteButtonConfigType;
    /** 删除按钮中的checkBox为true时执行方法(handleDelete执行之前执行) */
    beforeHandleDeleteCheckBox?: Function;
    /** 删除按钮中的checkBox为true时执行方法(handleDelete执行成功后才执行) */
    afterHandleDeleteCheckBox?: Function;
    /** 覆盖删除方法 */
    coverHandleDelete?: Function;
    /** 后台服务地址 */
    serviceUrl?: string;
    /** 后台服务数据 */
    serviceId?: serviceIdType;
    /** 显示日志按钮 */
    showLogger?: {
        /** 日志显示 */
        loggerDisplay?: boolean;
        /** 单个数据日志显示 */
        loggerSingleDisplay?: boolean;
        /** 日志类型 */
        logType?: LogType;
        /** 日志查询类型 */
        loggerQueryType?: string;
        /** 锁定选择日志类型 */
        isLockLoggerTypeSelect?: boolean;
        /** 锁定选择日志日期 */
        isLockLoggerDateSelect?: boolean;
    };
};

/** 刷新数据的类型 */
export declare type initDataType = {
    /** 服务id */
    apiId?: string;
    /** 页面传送的数据 */
    sendPageData?: any;
};


// ------------------- 组件主体 -------------------
/**
 * 通用Web组件 WebUniversal
 * 提供 CRUD 表格、查询、排序、展开、上传、日志等通用功能
 * @param props 外部配置（所有表格相关属性放在 props.props 中）
 * @param ref 对外暴露的方法
 */
const WebUniversal = forwardRef((props: any, ref: any) => {
    // ===== Redux 状态 =====
    const showPageData = useSelector((state: any) => state.showPageData);
    const dispatch = useDispatch();

    // ===== 解构外部配置 =====
    const extendProps: WUExtendPropsType = props.props;
    const {
        arrayFilesName,
        EditComponent,
        UploadFileComponent,
        titleName,
        apiId,
        showUploadFile,
        showAdd,
        showQuery,
        queryTitle,
        showImage,
        showUseBak,
        showStates,
        showOperation,
        showDelete,
        showEdit,
        showPrint,
        disabledAdd,
        disabledUpload,
        uploadSendData,
        sendPageData,
        isExpandable,
        summaryRender,
        expandableDataName,
        isExpandableDateIncludingMasterData,
        deleteButtonConfig,
        beforeHandleDeleteCheckBox,
        afterHandleDeleteCheckBox,
        coverHandleDelete,
        serviceUrl,
        serviceId: outerServiceId,
        showLogger: rawShowLogger = {},
    } = extendProps;

    // 主键名（默认为 'id'）
    const primaryKeyName = extendProps.primaryKeyName || 'id';
    // 服务标识（默认为 'bm'）
    const serviceId = outerServiceId || 'bm';
    // 实际请求的服务地址
    const defaultService =systematic.wmService;

    // 日志配置
    const showLogger = rawShowLogger;
    const {
        loggerDisplay,
        loggerSingleDisplay,
        logType,
        loggerQueryType,
        isLockLoggerTypeSelect = true,
        isLockLoggerDateSelect = false,
    } = showLogger;

    // ===== 组件内部状态 =====
    const [modalVisible, setModalVisible] = useState(false);       // 编辑/新增弹窗
    const [modalUploadVisible, setModalUploadVisible] = useState(false); // 文件上传弹窗
    const [modalLogger, setModalLogger] = useState(false);         // 日志弹窗
    const [modalData, setModalData] = useState({});                // 弹窗携带的数据
    const [loggerQueryId, setLoggerQueryId] = useState<string>();  // 日志查询的单条ID
    const [uploadFilesData, setUploadFilesData] = useState<any>(); // 上传文件所需数据
    const [queryInput, setQueryInput] = useState(showPageData.queryString); // 查询输入框内容
    const [useBak, setUseBak] = useState<boolean>(true);           // 是否使用备份数据
    const [expandableColumnsData, setExpandableColumnsData] = useState<any>(); // 子表格列定义
    const [expandedData, setExpandedData] = useState<any>();       // 子表格数据缓存

    // 用于记录最近一次 InitData 的初始状态（跨渲染保持）
    const latestInitStateRef = useRef<any>(null);
    // loading 锁，防止短时间内重复分页/排序请求（使用 ref 跨渲染保持）
    const loadingRef = useRef(false);

    // ===== 从 Redux 中提取当前页数据 =====
    let pageData: any = {
        pageSize: 10,
        current: 1,
        queryString: undefined,
        total: 0,
        bakTotal: 0,
        useBak: true,
        rows: undefined,
        extendData: {},
    };

    try {
        if (typeof showPageData[apiId] !== 'undefined') {
            pageData = showPageData[apiId].data;
        }
    } catch { }

    const { pageSize, current, total, rows } = pageData;

    // ===== 构造初始请求参数对象的工具函数 =====
    /**
     * 创建 InitData 所需的基础 state 片段
     * @param overrides 覆盖默认 data 字段的内容
     */
    const createInitState = (overrides?: any) => {
        const base = {
            [apiId]: {
                data: {
                    current: 1,
                    pageSize: 10,
                    queryString: '',
                    orderByString: '',
                    useBak: true,
                    ...overrides?.data,
                },
                fetchUrl: defaultService,
                sendPageData,
                ...overrides,
            },
        };
        // 保留 apiId 顶层字段（某些逻辑依赖）
        base.apiId = apiId;
        return base;
    };

    // ===== 初始化 & 副作用 =====
    useEffect(() => {
        // 首次加载时初始化数据
        const initialState = createInitState();
        latestInitStateRef.current = initialState;
        dispatch(InitData(initialState));
        // 设置子表格列
        setExpandableColumnsData(extendProps.expandableColumns);
    }, []); // 仅在挂载时执行

    useEffect(() => {
        loadingRef.current = false;
    }, [rows, current, total]);

    // ===== 事件处理函数 =====
    /** 修改单条记录的单个字段（常用于开关类操作） */
    const handleChangeOne = useCallback(
        async (id: any, columnName: string, value: boolean, targetApiId: string) => {
            const bodys = {
                apiId: targetApiId,
                apiVariable: id,
                apiExtend: 'updateOne',
                apiData: { columnName, value },
            };
            if (await fetchApi.PUT(defaultService, bodys)) {
                antMessage(
                    'success',
                    'updateOne编辑成功',
                    `apiId：${bodys.apiId}，columnName：${bodys.apiData.columnName}，value：${bodys.apiData.value}`
                );
                dispatch(InitData({ ...showPageData }));
                return true;
            } else {
                antMessage('error', '编辑失败', 'error');
                return false;
            }
        },
        [defaultService, dispatch, showPageData]
    );

    /** 删除记录 */
    const handleDelete = useCallback(
        async (record: any, isChecked: boolean) => {
            // 若有外部覆盖的删除方法，则直接调用
            if (coverHandleDelete) {
                coverHandleDelete(record, isChecked);
                return;
            }

            const id = record[primaryKeyName];
            if (isChecked && beforeHandleDeleteCheckBox) {
                await beforeHandleDeleteCheckBox(record);
            }

            fetchApi
                .DELETE(defaultService, { apiId, apiVariable: id })
                .then(async (res) => {
                    if (res.success) {
                        if (isChecked && afterHandleDeleteCheckBox) {
                            await afterHandleDeleteCheckBox(record);
                        }
                        antMessage('success', '删除成功', 'success');
                        dispatch(InitData({ ...showPageData }));
                    } else {
                        antMessage('error', '删除失败', 'error');
                    }
                })
                .catch((err) => {
                    antMessage('error', '删除失败', `${err.message}，[${err.errors.status}]`);
                });
        },
        [
            coverHandleDelete,
            primaryKeyName,
            beforeHandleDeleteCheckBox,
            afterHandleDeleteCheckBox,
            defaultService,
            apiId,
            dispatch,
            showPageData,
        ]
    );

    /** 打开新增/编辑弹窗 */
    const openModal = useCallback(
        (type: string, id: string) => {
            setModalData({ type, id, sendPageData });
            setModalVisible(true);
        },
        [sendPageData]
    );

    /** 编辑按钮触发 */
    const handleEdit = useCallback(
        (recordData: any) => {
            openModal('EDIT', recordData[primaryKeyName]);
        },
        [openModal, primaryKeyName]
    );

    /** 关闭编辑弹窗的回调 */
    const onCloseModal = useCallback(
        (state: boolean) => {
            setModalVisible(false);
            if (state) {
                // 编辑成功后刷新数据
                dispatch(InitData({ ...showPageData }));
            }
        },
        [dispatch, showPageData]
    );

    /** 文件上传按钮触发 */
    const hanldUpload = useCallback(
        (e: any) => {
            setUploadFilesData({ id: e, disabledUpload, uploadSendData });
            setModalUploadVisible(true);
        },
        [disabledUpload, uploadSendData]
    );

    /** 关闭上传弹窗 */
    const onCloseUpload = useCallback(
        (state: boolean) => {
            setModalUploadVisible(false);
            // 上传成功后刷新
            dispatch(InitData({ ...showPageData }));
        },
        [dispatch, showPageData]
    );

    /** 排序变化 */
    const handleSort = useCallback(
        (sort: any) => {
            let orderByString = '';
            switch (sort.order) {
                case 'descend':
                    orderByString = `${sort.field}@DESC`;
                    break;
                case 'ascend':
                    orderByString = `${sort.field}@ASC`;
                    break;
                default:
                    break;
            }
            if (!loadingRef.current) {
                loadingRef.current = true;
                const state = createInitState({ data: { orderByString } });
                dispatch(InitData(state));
            }
        },
        [dispatch, defaultService, sendPageData, apiId]
    );

    /** 查询触发（回车或点击查询按钮） */
    const queryData = useCallback(
        (queryString: any) => {
            const safeQuery = typeof queryString === 'undefined' ? '' : queryString;
            const state = createInitState({
                data: {
                    queryString: safeQuery,
                    current: pageData.queryString === safeQuery ? pageData.current : 1,
                },
            });
            latestInitStateRef.current = state;
            dispatch(InitData(state));
        },
        [dispatch, pageData.current, pageData.queryString, createInitState]
    );

    /** 输入框变化 */
    const handleQueryInputChange = useCallback((e: any) => {
        setQueryInput(e.target.value);
    }, []);

    /** 分页改变 */
    const changePage = useCallback(
        (newPage: number, newPageSize: number) => {
            if (!loadingRef.current) {
                loadingRef.current = true;
                const state = createInitState({ data: { current: newPage, pageSize: newPageSize } });
                dispatch(InitData(state));
            }
        },
        [dispatch, createInitState]
    );

    /** pageSize 改变 */
    const changePageSize = useCallback(
        (newSize: number, newPage: number) => {
            if (!loadingRef.current) {
                loadingRef.current = true;
                const state = createInitState({ data: { current: newPage, pageSize: newSize } });
                dispatch(InitData(state));
            }
        },
        [dispatch, createInitState]
    );

    /** 展开子表格 */
    const onExpandClick = useCallback(
        (status: boolean, item: any) => {
            if (!status) return;
            let expandableData = expandableDataName ? item[expandableDataName] : item;
            if (isExpandableDateIncludingMasterData) {
                if (Array.isArray(expandableData)) {
                    expandableData = expandableData.map((row) => ({ ...row, $masterData: item }));
                } else {
                    expandableData = { ...expandableData, $masterData: item };
                }
            }
            setExpandedData((prev: any) => ({
                ...prev,
                [item[primaryKeyName]]: expandableData,
            }));
        },
        [expandableDataName, isExpandableDateIncludingMasterData, primaryKeyName]
    );

    /** 切换使用备份数据 */
    const handleUseBakChange = useCallback(
        (e: any) => {
            const checked = e.target.checked;
            setUseBak(checked);
            const state = createInitState({ data: { useBak: checked } });
            dispatch(InitData(state));
        },
        [dispatch, createInitState]
    );

    /** 打开日志弹窗（全局日志或单条日志） */
    const openLogger = useCallback(
        (singleId?: string) => {
            setLoggerQueryId(singleId);
            setModalLogger(true);
        },
        []
    );

    /** 打印功能（原实现保留） */
    const handlePrint = useCallback((id: string) => {
        const head = document.getElementsByTagName('head')[0];
        const styles = head.getElementsByTagName('style');
        // 注意：原代码中的 cssLightData/cssDarkData 已移除，因为并未实际赋值使用，
        // 此处保留原有结构但移除无效逻辑，如需恢复请自行补充样式数组。
        window.document.body.innerHTML = window.document.getElementById(id)?.innerHTML!;
        window.print();
        window.location.reload();
    }, []);

    // ===== 对外暴露的方法（useImperativeHandle） =====
    useImperativeHandle(ref, () => ({
        /** 清空当前页面数据 */
        wipeData: () => {
            const state = createInitState();
            dispatch(WipeData(state));
        },
        /** 外部调用修改单条记录 */
        handleChangeOne,
        /** 外部触发数据重新加载 */
        initData: (currentData: initDataType) => {
            if (currentData) {
                if (currentData.apiId) {
                    showPageData.apiId = currentData.apiId;
                    showPageData[currentData.apiId].data = latestInitStateRef?.current[currentData.apiId]?.data;
                    if (currentData.sendPageData) {
                        showPageData[currentData.apiId].sendPageData = currentData.sendPageData;
                    }
                }
            }
            dispatch(InitData({ ...showPageData }));
        },
        /** 动态设置子表格列定义 */
        setExpandableColumns: (exColumns: any) => {
            setExpandableColumnsData(exColumns);
        },
        /** 外部修改 useBak 状态（兼容旧版） */
        setState: (options: { useBak: boolean }) => {
            setUseBak(options.useBak);
            // 注意：这里仅修改本地 UI 状态，未自动触发数据重载，与原实现一致
        },
        /** 暴露服务地址供外部使用 */
        defaultService,
    }));

    // ===== 表格列配置（注入行为） =====
    const behaviorsData: behaviorsDataType = {
        showImage,
        showUploadFile,
        showStates,
        showOperation,
        showDelete,
        showEdit,
        hanldUpload,
        handleDelete,
        handleEdit,
        handleChangeOne,
        apiId,
        primaryKeyName,
        behaviorsColumns: extendProps.columns,
        arrayFilesName,
        deleteButtonConfig,
        loggerSingleDisplay,
        handleLoggerSingle: (id: string) => openLogger(id),
    };

    const columns = WebBehaviors(behaviorsData);

    // ===== 分页配置 =====
    const paginationProps = {
        showSizeChanger: true,
        showQuickJumper: false,
        showTotal: () => `共${total}条`,
        pageSize,
        current,
        total,
        onShowSizeChange: (cur: number, size: number) => changePageSize(size, cur),
        onChange: (cur: number) => changePage(cur, pageSize),
    };

    // ===== 子表格渲染函数 =====
    const getExpandedRowRender = (item: any) => {
        if (!expandedData) return undefined;
        const rowData = expandedData[item[primaryKeyName]];
        const dataSource = Array.isArray(rowData) ? rowData : rowData ? [rowData] : undefined;
        return (
            <Table
                columns={expandableColumnsData}
                rowKey={(row: any) => row[primaryKeyName] || row.id}
                pagination={false}
                dataSource={dataSource}
            />
        );
    };

    // ===== 合计栏 =====
    const renderSummary = () => {
        if (!summaryRender) return null;
        const sumData = pageData?.extendData?.[summaryRender.sumDataName];
        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>总计:</Table.Summary.Cell>
                <Table.Summary.Cell index={0}>{sumData}</Table.Summary.Cell>
            </Table.Summary.Row>
        );
    };

    // ===== 渲染 =====
    return (
        <>
            {/* ---------- 按钮栏 ---------- */}
            <Space style={{ marginBottom: 16 }}>
                {/* 新增按钮 */}
                {(showAdd === true || typeof showAdd === 'undefined') && (
                    <Button disabled={disabledAdd} onClick={() => openModal('NEW', '')}>
                        新增{titleName}
                    </Button>
                )}

                {/* 查询区域 */}
                {(showQuery === true || typeof showQuery === 'undefined') && (
                    <>
                        <Tooltip placement="topLeft" title={queryTitle}>
                            <Input
                                placeholder="请输入查询内容"
                                defaultValue={queryInput}
                                onChange={handleQueryInputChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') queryData(queryInput);
                                }}
                            />
                        </Tooltip>
                        <Button
                            icon={<SearchOutlined />}
                            onClick={() => queryData(queryInput)}
                        >
                            查询
                        </Button>
                    </>
                )}

                {/* 打印与导出 */}
                {showPrint && (
                    <>
                        <Button onClick={() => handlePrint('table')}>打印</Button>
                        <Button onClick={() => DownloadExcel(props, rows)}>下载EXCEL文件</Button>
                    </>
                )}

                {/* 使用备份数据勾选框 */}
                {showUseBak && (
                    <Tooltip placement="topLeft" title="获取数据时同时使用备份数据">
                        <Checkbox checked={useBak} onChange={handleUseBakChange}>
                            备份数据
                        </Checkbox>
                    </Tooltip>
                )}

                {/* 日志按钮 */}
                {loggerDisplay && (
                    <Button onClick={() => openLogger()}>日志</Button>
                )}
            </Space>

            {/* ---------- 主表格 ---------- */}
            <Table
                id="table"
                rowKey={(row: any) => row[primaryKeyName]}
                columns={columns}
                dataSource={rows}
                pagination={paginationProps}
                size="small"
                onChange={(_a, _b, par) => handleSort(par)}
                expandable={
                    isExpandable
                        ? {
                            expandedRowRender: getExpandedRowRender,
                            onExpand: onExpandClick,
                        }
                        : undefined
                }
                summary={renderSummary}
            />

            {/* ---------- 模态框 ---------- */}
            {/* 编辑/新增弹窗 */}
            {modalVisible && <EditComponent closeModal={onCloseModal} data={modalData} />}

            {/* 文件上传弹窗 */}
            {modalUploadVisible && (
                <UploadFileComponent onClose={onCloseUpload} data={uploadFilesData} />
            )}

            {/* 日志弹窗 */}
            {modalLogger && (
                <WebUniversalLogger
                    closeModal={() => setModalLogger(false)}
                    data={{
                        logType,
                        queryType: loggerQueryType || apiId,
                        queryId: loggerQueryId,
                        isQuery: true,
                        isLockLoggerTypeSelect,
                        isLockLoggerDateSelect,
                    }}
                />
            )}
        </>
    );
});

export default WebUniversal;